import { ensureDate, formatDate } from '@helpers/date';
import { formatCurrency } from '@helpers/utils';
import { withRedux } from '@hoc/withRedux';
import SvgCalendar from '@img/icons/calendar.svg';
import { basket } from '@redux/slices/basket';
import { addProductToBasket } from '@redux/slices/basket/actions/addProductToBasket';
import { retrieveBasket } from '@redux/slices/basket/actions/retrieveBasket';
import { product } from '@redux/slices/product';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { isFulfilled } from '@reduxjs/toolkit';
import Button from '@stories/Components/Global/Buttons/Button/Button';
import LinkButton from '@stories/Components/Global/Buttons/LinkButton/LinkButton';
import TabButton from '@stories/Components/Global/Buttons/TabButton/TabButton';
import Datepicker from '@stories/Components/Global/DatePicker/DatePicker';
import FormField from '@stories/Components/Global/Forms/FormField';
import FormRow from '@stories/Components/Global/Forms/FormRow';
import Textarea from '@stories/Components/Global/Inputs/Textarea/Textarea';
import Textbox from '@stories/Components/Global/Inputs/Textbox/Textbox';
import Modal from '@stories/Components/Global/Modal/Modal';
import QuantitySelector from '@stories/Components/Global/QuantitySelector/QuantitySelector';
import { addBusinessDays } from 'date-fns';
import * as React from 'react';
import { ToastContainer, toast } from 'react-toastify';
import S from './HireSaleSelector.styles';
const HireSaleSelector = (props) => {
    const dispatch = useAppDispatch();
    const { basket: basketState, product: productState } = useAppSelector((state) => state);
    const productData = !productState.master
        ? props.initialData
        : { variant: productState.variant, sibling: productState.sibling };
    const active = !productState.master ? props.initialData.active : productState.active;
    const activeProduct = productData[active];
    const [open, setOpen] = React.useState(false);
    const [qty, setQty] = React.useState(1);
    const [onDate, setOnDate] = React.useState(basketState.onDate ?? addBusinessDays(new Date(), activeProduct?.calendarLeadTime ?? 5));
    // End date for the default value was being set one day too long so minusing 1 day to get an accurate amount of days.
    const [offDate, setOffDate] = React.useState(basketState.offDate ??
        addBusinessDays(new Date(), (activeProduct?.minimumPeriod ?? 5) + (activeProduct?.calendarLeadTime ?? 5) - 1));
    const [accessoryText, setAccessoryText] = React.useState('');
    React.useEffect(() => {
        loadBasket();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!activeProduct) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(S.Container, { "data-site": props.site, "data-logged-in": props.account.accountType !== 'none' },
            React.createElement(S.AccountPromptWrapper, null,
                React.createElement(S.AccountPrompt, null,
                    React.createElement(S.AccountTitle, null, props.account.title),
                    props.account.content && React.createElement(S.AccountContent, null, props.account.content),
                    React.createElement(S.AccountCtaList, null, props.account.ctas.map((cta, i) => (React.createElement("li", { key: `account-prompt-cta-${i}` },
                        React.createElement(LinkButton, { buttonType: props.account.accountType === 'none' && i === 0
                                ? 'secondary'
                                : props.site === 'mep'
                                    ? 'link'
                                    : 'link-alt', href: cta.url, target: cta.target, site: props.site }, cta.text))))))),
            React.createElement(S.Label, null, activeProduct.name),
            activeProduct.g42Id && props.site === 'mep' && (React.createElement(S.Sku, { "data-bottom-spacing": true },
                React.createElement("strong", null, "MEP Code:"),
                " ",
                activeProduct.g42Id)),
            React.createElement(S.ProductTabs, null, props.site === 'ess' ? (React.createElement(React.Fragment, null,
                productData.variant && (React.createElement(TabButton, { active: activeProduct.type === 'hire', ...(productData.variant ? { onClick: () => setActive('variant') } : {}) }, "Hire")),
                productData.sibling && (React.createElement(TabButton, { active: activeProduct.type === 'purchase', onClick: () => setActive('sibling') }, "Purchase")))) : (React.createElement(TabButton, { active: true }, activeProduct.type === 'hire' ? 'Hire' : 'Purchase'))),
            React.createElement(S.SelectorWrapper, null,
                renderPriceAndQtySelector(true),
                (activeProduct.price.isValid ?? true) && (React.createElement(Button, { buttonType: props.site === 'mep' ? 'default' : 'secondary', site: props.site, disabled: props.site === 'mep' && props.account.accountType === 'none', onClick: () => setOpen(true) }, props.buttonLabel)),
                props.scrollLink && props.scrollLink.id ? (React.createElement(S.ScrollLinkContainer, null,
                    React.createElement(S.Label, null, props.scrollLink.label),
                    React.createElement(Button, { buttonType: "link-alt", onClick: () => scrollToSection(props.scrollLink?.id ?? ''), site: props.site }, props.scrollLink.text))) : null)),
        React.createElement(Modal, { site: props.site, showClose: false, ariaLabel: `${props.buttonLabel} Modal`, isOpen: open, showBackground: true, onClose: () => setOpen(false), closeOnBackground: true },
            React.createElement(S.AddToBasketModal, null,
                React.createElement(S.AddToBasketModalContent, null,
                    renderPriceAndQtySelector(false),
                    React.createElement(FormRow, { "data-layout": "full" },
                        React.createElement(FormField, { name: "accessory", label: "Additional Comments" },
                            React.createElement(Textarea, { placeholder: "Additional Comments", value: accessoryText, onChange: (e) => setAccessoryText(e.target.value) }))),
                    activeProduct.type === 'hire' && (React.createElement(Datepicker, { label: "Hire Dates", onStartDateChanged: setOnDate, onEndDateChanged: setOffDate, leadTimeDays: activeProduct.calendarLeadTime, endDate: ensureDate(offDate), startDate: ensureDate(onDate), excludedDates: props.excludedDates })),
                    React.createElement(S.ModalButtonWrapper, null, (activeProduct.price.isValid ?? true) && (React.createElement(Button, { buttonType: props.site === 'mep' ? 'default' : 'secondary', site: props.site, disabled: props.site === 'mep' && props.account.accountType === 'none', onClick: () => {
                            addToBasket(activeProduct.id, qty, accessoryText, activeProduct.type === 'hire' ? formatDate(onDate, 'yyyy-MM-dd') : undefined, activeProduct.type === 'hire' ? formatDate(offDate, 'yyyy-MM-dd') : undefined, activeProduct.documentId);
                            setOpen(false);
                        } }, props.buttonLabel)))))),
        React.createElement(ToastContainer, { autoClose: 5000, position: "bottom-right" })));
    async function loadBasket() {
        dispatch(retrieveBasket());
    }
    async function addToBasket(id, qty, accessoryText, onDate, offDate, documentId) {
        dispatch(basket.actions.setStatus('submitting'));
        const result = await dispatch(addProductToBasket({ products: [{ id, qty }], accessoryText, onDate, offDate, documentId }));
        /** Show cart overlay, or error message */
        if (isFulfilled(result) && result.payload) {
            if (result.payload.errors) {
                toast(result.payload.errors.join(' '), { type: 'error' });
            }
            else {
                dispatch(basket.actions.setDrawerStatus('open-new'));
            }
        }
        else {
            dispatch(basket.actions.setStatus('rejected'));
            toast(`Error adding item to basket, please try again`, { type: 'error' });
        }
    }
    async function setActive(active) {
        dispatch(product.actions.setActiveProductType(active));
    }
    function scrollToSection(id) {
        const targetEl = document.getElementById(id);
        if (!targetEl) {
            return;
        }
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
    function renderPriceAndQtySelector(showDateFields) {
        return (React.createElement(React.Fragment, null,
            React.createElement(S.PriceQtyWrapper, null,
                props.site === 'ess' ||
                    (props.site === 'mep' && props.account.accountType === 'credit') ? (React.createElement(React.Fragment, null,
                    React.createElement(S.LabelWrapper, null,
                        React.createElement(S.Label, null,
                            props.site === 'ess' && props.account.accountType !== 'none' ? 'Your ' : '',
                            activeProduct.type,
                            " Price"),
                        activeProduct.g42Id && (props.site === 'ess' || !showDateFields) && (React.createElement(S.Sku, null,
                            React.createElement("strong", null,
                                props.site.toLocaleUpperCase(),
                                " Code:"),
                            " ",
                            activeProduct.g42Id))),
                    React.createElement("span", null,
                        React.createElement(S.PriceWrapper, null,
                            React.createElement(S.Price, null, activeProduct.price.isValid ?? true
                                ? formatCurrency(activeProduct.price.incl)
                                : 'Invalid price'),
                            ' ',
                            React.createElement(S.Suffix, null, activeProduct.price.isValid ?? true ? activeProduct.price.suffix : '')),
                        activeProduct.waiverText && React.createElement(S.VatText, null, activeProduct.waiverText),
                        React.createElement(S.VatText, null,
                            formatCurrency(activeProduct.price.excl),
                            " Ex. VAT")))) : (React.createElement(React.Fragment, null, props.account.accountType === 'none' && (React.createElement("span", null,
                    React.createElement(S.Label, null,
                        React.createElement(React.Fragment, null, "Please sign in to view prices and place an order")),
                    React.createElement(LinkButton, { buttonType: "link-alt", href: props.signInUrl, site: 'mep' }, "Sign In"))))),
                React.createElement(QuantitySelector, { value: qty, onDownPress: () => setQty(qty - 1), onUpPress: () => setQty(qty + 1), site: props.site, disabled: props.site === 'mep' && props.account.accountType === 'none' })),
            props.site === 'ess' || (props.site === 'mep' && props.account.accountType !== 'none') ? (React.createElement(S.DateWaiverWrapper, null,
                showDateFields && activeProduct.type === 'hire' && (React.createElement(FormRow, null,
                    React.createElement(FormField, { name: "onDate", label: "On Hire Date", required: true },
                        React.createElement(Textbox, { placeholder: "DD/MM/YYYY", type: "text", value: onDate ? formatDate(onDate, 'dd/MM/yyyy') : undefined, readOnly: true, onClick: () => setOpen(true) }),
                        React.createElement(S.CalendarIcon, null,
                            React.createElement(SvgCalendar, null))),
                    React.createElement(FormField, { name: "offDate", label: "Off Hire Date", required: true },
                        React.createElement(Textbox, { placeholder: "DD/MM/YYYY", type: "text", value: offDate ? formatDate(offDate, 'dd/MM/yyyy') : undefined, readOnly: true, onClick: () => setOpen(true) }),
                        React.createElement(S.CalendarIcon, null,
                            React.createElement(SvgCalendar, null))))),
                activeProduct.type === 'hire' && !!activeProduct.minimumPeriod && (React.createElement(S.Waiver, null,
                    "Minimum hire period: ",
                    activeProduct.minimumPeriod,
                    " day",
                    activeProduct.minimumPeriod > 1 && React.createElement(React.Fragment, null, "s"))),
                !!activeProduct.displayLeadTime && (React.createElement(S.Waiver, null,
                    "Lead time: ",
                    activeProduct.displayLeadTime,
                    " day",
                    activeProduct.displayLeadTime > 1 && React.createElement(React.Fragment, null, "s"))),
                activeProduct.type === 'hire' && props.hireDisclaimer && (React.createElement(S.VatText, null, props.hireDisclaimer)))) : null));
    }
};
export default withRedux(HireSaleSelector);
